"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Mount Kailash phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.
// Times ABSOLUTE seconds on the assembled narration (jyotirlinga — no opener shift). 1280×704 @ 16fps.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 31 selective phrases (Yash-approved 2026-06-17). Latin numerals (50, 2000m, 19,500, 221, 2000m);
// dharma names in Devanagari. Anchored to word_timestamps_hindi.
export const MK_PHRASES: Phrase[] = [
  { start: 14.0,   out: 17.4,   lines: [[{ t: "चार", c: "y" }, { t: "धर्म", c: "w", big: true }]] },
  { start: 34.9,   out: 38.4,   lines: [[{ t: "चढ़ना", c: "y" }], [{ t: "बैन", c: "r", big: true }, { t: "कर", small: true }, { t: "दिया", c: "r" }]] },
  { start: 51.0,   out: 54.4,   lines: [[{ t: "बूढ़ा", c: "r", big: true }], [{ t: "बना", c: "y" }, { t: "देती", small: true }, { t: "है", c: "y" }]] },
  { start: 70.0,   out: 73.6,   lines: [[{ t: "मीठा", c: "y" }, { t: "और", small: true }, { t: "नमकीन", c: "w", big: true }]] },
  { start: 118.6,  out: 122.0,  lines: [[{ t: "सनातन", c: "y" }, { t: "धर्म", c: "w", big: true }]] },
  { start: 147.3,  out: 150.7,  lines: [[{ t: "बौद्ध", c: "y" }, { t: "धर्म", c: "w", big: true }]] },
  { start: 166.0,  out: 169.4,  lines: [[{ t: "बोन", c: "y" }, { t: "धर्म", c: "w", big: true }]] },
  { start: 198.0,  out: 201.4,  lines: [[{ t: "जैन", c: "y" }, { t: "धर्म", c: "w", big: true }]] },
  { start: 208.3,  out: 211.8,  lines: [[{ t: "50", c: "w", big: true }, { t: "km", c: "w", small: true }], [{ t: "परिक्रमा", c: "y" }]] },
  { start: 224.8,  out: 228.2,  lines: [[{ t: "क्लॉकवाइज", c: "y", big: true }]] },
  { start: 241.7,  out: 245.1,  lines: [[{ t: "उल्टी", c: "r", big: true }, { t: "दिशा", c: "w" }]] },
  { start: 293.0,  out: 296.4,  lines: [[{ t: "खोखली", c: "y" }, { t: "दुनिया", c: "w", big: true }]] },
  { start: 330.2,  out: 333.7,  lines: [[{ t: "मानव", c: "y" }, { t: "निर्मित", small: true }], [{ t: "पिरामिड", c: "w", big: true }]] },
  { start: 367.7,  out: 371.1,  lines: [[{ t: "शम्भाला", c: "y", big: true }]] },
  { start: 388.8,  out: 392.2,  lines: [[{ t: "यती", c: "r", big: true }]] },
  { start: 416.2,  out: 419.9,  lines: [[{ t: "एवरेस्ट", c: "w" }, { t: "से", small: true }], [{ t: "2000m", c: "r", big: true }, { t: "छोटा", c: "y" }]] },
  { start: 446.0,  out: 450.0,  lines: [[{ t: "19,500", c: "w", big: true }, { t: "ft", small: true }]] },
  { start: 515.7,  out: 519.2,  lines: [[{ t: "महादेव", c: "y" }, { t: "का", small: true }, { t: "रूप", c: "w", big: true }]] },
  { start: 676.6,  out: 680.2,  lines: [[{ t: "राइनहोल्ड", c: "y" }], [{ t: "मेसनर", c: "w", big: true }]] },
  { start: 711.0,  out: 714.4,  lines: [[{ t: "चीन", c: "w" }, { t: "झुक", c: "r", big: true }, { t: "गया", c: "r" }]] },
  { start: 760.4,  out: 763.8,  lines: [[{ t: "मानसरोवर", c: "y", big: true }]] },
  { start: 793.8,  out: 797.2,  lines: [[{ t: "राक्षस", c: "r" }, { t: "ताल", c: "w", big: true }]] },
  { start: 852.0,  out: 855.4,  lines: [[{ t: "पर्वत", c: "y" }], [{ t: "उठाया", c: "w", big: true }]] },
  { start: 898.9,  out: 902.4,  lines: [[{ t: "शिव", c: "y" }, { t: "तांडव", c: "w", big: true }], [{ t: "स्तोत्रम", c: "y" }]] },
  { start: 909.3,  out: 912.7,  lines: [[{ t: "चंद्रहास", c: "w", big: true }]] },
  { start: 991.7,  out: 995.1,  lines: [[{ t: "उम्र", c: "y" }, { t: "चूस", c: "r", big: true }], [{ t: "लेता", small: true }, { t: "है", c: "y" }]] },
  { start: 1070.5, out: 1074.0, lines: [[{ t: "बूढ़े", c: "r" }, { t: "होकर", small: true }], [{ t: "मर", c: "r", big: true }, { t: "गए", c: "r" }]] },
  { start: 1081.4, out: 1084.9, lines: [[{ t: "एक्सिस", c: "y" }, { t: "मुंडी", c: "w", big: true }]] },
  { start: 1127.6, out: 1131.0, lines: [[{ t: "मालपा", c: "w", big: true }]] },
  { start: 1168.0, out: 1171.5, lines: [[{ t: "221", c: "r", big: true }, { t: "लोग", c: "w" }], [{ t: "मरे", c: "r", big: true }]] },
  { start: 1237.6, out: 1240.6, lines: [[{ t: "चार", c: "w", big: true }, { t: "नदियां", c: "y" }]] },
  { start: 1240.8, out: 1245.5, lines: [[{ t: "सिंधु,", c: "y" }, { t: "सतलुज,", c: "w" }], [{ t: "ब्रह्मपुत्र,", c: "y" }, { t: "कर्णाली", c: "w" }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-RIGHT viewing tip — Kamakhya style: two lines, yellow "Watch at 1.2x" + smaller white
// "for best experience". NO holes mask. 0→5s only.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, right: PAD, fontFamily, opacity: op,
                  display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x
      </span>
      <span style={{ color: WHITE, fontSize: Math.round(SMALL_FS * 0.7), fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        for best experience
      </span>
    </div>
  );
};

// Bottom-LEFT "आगे देखिए" teaser — sized to match the watch tip (SMALL_FS), yellow text, WHITE
// cycling dots ("" → . → .. → ...). Shifted DOWN to the bottom baseline. NO holes mask. 0→90s.
const AageDekhiye: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.5, out - 0.6, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const n = Math.floor((t * 1.5) % 4); // 0,1,2,3 dots (Kamakhya cadence)
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        आगे देखिए<span style={{ display: "inline-block", width: "1.4em", textAlign: "left", color: WHITE }}>{".".repeat(n)}</span>
      </span>
    </div>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (आगे देखिए teaser, watch tip, LS pills).
export const MountKailashOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = MK_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* आगे देखिए teaser (bottom-left, cycling dots) over the opening hook */}
      <AageDekhiye fps={fps} start={0.0} out={90.0} />

      {/* viewing tip (bottom-left, half-size) */}
      <WatchTag fps={fps} start={0.0} out={5.0} />

      {/* Like+Subscribe — LS2 (bell) @ "सब्सक्राइब" ~90s · LS5 (no bell) @ outro "सब्सक्राइब" ~1316s */}
      <Sequence from={S(90.0)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(1316.0)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
